import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Rating,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BusinessIcon from '@mui/icons-material/Business';

export const ClientOpinion = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const autoPlayRef = useRef();

  // دوال مساعدة للـ Avatar
  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // خذ الحرف الأول من الاسم الأول فقط
      return nameParts[0][0].toUpperCase();
    }
    // لو اسم واحد، خذ أول حرف فقط
    return name.substring(0, 1).toUpperCase();
  };

  const maskSecondName = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // الاسم الأول كامل + الحرف الأول من الاسم الثاني + نقاط
      const firstName = nameParts[0];
      const secondNameFirstLetter = nameParts[1][0];
      return `${firstName} ${secondNameFirstLetter}.`;
    }
    return name;
  };

  // لون موحد للـ Avatar - أزرق ثابت
  const AVATAR_COLOR = '#4361ee'; // أزرق ثابت

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Testimonials data
  const testimonialsData = {
    en: [
      {
        id: 1,
        name: 'Ahmed Al Mansoori',
        position: 'Marketing Manager',
        rating: 5,
        comment: 'Almaria Legal Consultancy provided exceptional service in helping us set up our business in Dubai. Their team was professional, knowledgeable, and guided us through every step of the process. Highly recommended!',
        date: 'March 2024',
        company: 'Al Mansoori Group',
        icon: BusinessIcon
      },
      {
        id: 2,
        name: 'Fatima Al Zaabi',
        position: 'Legal Director, Tech Innovations',
        rating: 5,
        comment: 'I have been working with Almaria for over 3 years now. Their legal advice on UAE labor law and contract matters has been invaluable. They are responsive, accurate, and truly care about their clients.',
        date: 'February 2024',
        company: 'Tech Innovations',
        icon: BusinessIcon
      },
      {
        id: 3,
        name: 'Mohammed Al Falasi',
        position: 'Property Developer',
        rating: 5,
        comment: 'The team at Almaria helped me resolve a complex rental dispute quickly and efficiently. Their knowledge of Dubai property law is impressive. I will definitely use their services again.',
        date: 'January 2024',
        company: 'Falasi Properties',
        icon: BusinessIcon
      },
      {
        id: 4,
        name: 'Sara Al Hashimi',
        position: 'Entrepreneur',
        rating: 4,
        comment: 'Great service from start to finish. They helped me understand the legal requirements for my startup and handled all the paperwork. Very satisfied with their professionalism.',
        date: 'December 2023',
        company: 'Hashimi Ventures',
        icon: BusinessIcon
      },
      {
        id: 5,
        name: 'Khalid Al Nuaimi',
        position: 'Business Owner',
        rating: 5,
        comment: 'Excellent legal consultancy services. They helped me with contract reviews and provided valuable advice on business expansion. The team is very responsive and knowledgeable.',
        date: 'November 2023',
        company: 'Nuaimi Trading',
        icon: BusinessIcon
      },
      {
        id: 6,
        name: 'Noura Al Shamsi',
        position: 'HR Manager',
        rating: 5,
        comment: 'Almaria helped us draft employment contracts and employee handbooks that comply with UAE labor law. Their attention to detail and understanding of local regulations saved us from potential legal issues.',
        date: 'October 2023',
        company: 'Shamsi Industries',
        icon: BusinessIcon
      }
    ],
    ar: [
      {
        id: 1,
        name: 'أحمد المنصوري',
        position: 'الرئيس التنفيذي, مجموعة المنصوري',
        rating: 5,
        comment: 'قدمت شركة ألمارية للاستشارات القانونية خدمة استثنائية في مساعدتنا على تأسيس أعمالنا في دبي. كان فريقهم محترفاً وذو معرفة عالية، وأرشدونا خلال كل خطوة من العملية. أوصي بهم بشدة!',
        date: 'مارس ٢٠٢٤',
        company: 'مجموعة المنصوري',
        icon: BusinessIcon
      },
      {
        id: 2,
        name: 'فاطمة الزعابي',
        position: 'المدير القانوني, تك إنوفيشنز',
        rating: 5,
        comment: 'أعمل مع ألمارية منذ أكثر من ٣ سنوات. كانت استشاراتهم القانونية حول قانون العمل الإماراتي ومسائل العقود لا تقدر بثمن. هم متجاوبون ودقيقون ويهتمون حقاً بعملائهم.',
        date: 'فبراير ٢٠٢٤',
        company: 'تك إنوفيشنز',
        icon: BusinessIcon
      },
      {
        id: 3,
        name: 'محمد الفلاسي',
        position: 'مطور عقاري',
        rating: 5,
        comment: 'ساعدني فريق ألمارية في حل نزاع إيجاري معقد بسرعة وكفاءة. معرفتهم بقانون العقارات في دبي مثيرة للإعجاب. سأستخدم خدماتهم مرة أخرى بالتأكيد.',
        date: 'يناير ٢٠٢٤',
        company: 'فلاسي بروبرتيز',
        icon: BusinessIcon
      },
      {
        id: 4,
        name: 'سارة الهاشمي',
        position: 'رائدة أعمال',
        rating: 4,
        comment: 'خدمة رائعة من البداية إلى النهاية. ساعدوني في فهم المتطلبات القانونية لشركتي الناشئة وتعاملوا مع جميع الأوراق. راضية جداً عن احترافيتهم.',
        date: 'ديسمبر ٢٠٢٣',
        company: 'هاشمي فينتشرز',
        icon: BusinessIcon
      },
      {
        id: 5,
        name: 'خالد النعيمي',
        position: 'صاحب أعمال',
        rating: 5,
        comment: 'خدمات استشارية قانونية ممتازة. ساعدوني في مراجعة العقود وقدموا نصائح قيمة حول توسيع الأعمال. الفريق متجاوب وذو معرفة عالية.',
        date: 'نوفمبر ٢٠٢٣',
        company: 'نعيمي تريدينغ',
        icon: BusinessIcon
      },
      {
        id: 6,
        name: 'نورة الشامسي',
        position: 'مديرة موارد بشرية',
        rating: 5,
        comment: 'ساعدتنا ألمارية في صياغة عقود العمل وكتيبات الموظفين التي تتوافق مع قانون العمل الإماراتي. اهتمامهم بالتفاصيل وفهمهم للوائح المحلية أنقذنا من مشكلات قانونية محتملة.',
        date: 'أكتوبر ٢٠٢٣',
        company: 'شامسي إندستريز',
        icon: BusinessIcon
      }
    ]
  };

  const currentTestimonials = testimonialsData[currentLang];

  // Auto-play function
  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev + 3 >= currentTestimonials.length ? 0 : prev + 3
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev - 3 < 0 ? Math.max(0, currentTestimonials.length - 3) : prev - 3
    );
  };

  // Mobile navigation
  const nextMobile = () => {
    setCurrentIndex((prev) => (prev + 1) % currentTestimonials.length);
  };

  const prevMobile = () => {
    setCurrentIndex((prev) => (prev - 1 + currentTestimonials.length) % currentTestimonials.length);
  };

  // Auto-play effect for both mobile and desktop
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      if (isMobile) {
        nextMobile();
      } else {
        nextSlide();
      }
    }, 5000);

    return () => clearInterval(autoPlayRef.current);
  }, [isMobile, currentTestimonials.length]);

  // Handle touch events for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      nextMobile();
    }
    if (touchStart - touchEnd < -75) {
      prevMobile();
    }
  };

  // Get visible testimonials for desktop
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % currentTestimonials.length;
      visible.push(currentTestimonials[index]);
    }
    return visible;
  };

  return (
    <Box
      id="testimonials"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        color: 'white'
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                {currentLang === 'ar' ? 'آراء العملاء' : 'Client Testimonials'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 500,
                  mb: 3,
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                {currentLang === 'ar'
                  ? 'ماذا يقول عملاؤنا عن خدماتنا'
                  : 'What our clients say about our services'}
              </Typography>
              <Divider sx={{
                width: '80px',
                margin: '0 auto',
                height: '4px',
                background: 'linear-gradient(90deg, #FE6B8B 30%, #FF8E53 90%)',
                borderRadius: '2px'
              }} />
            </Box>
          </motion.div>

          {/* Desktop Carousel */}
          {!isMobile && (
            <Box sx={{ position: 'relative', px: 4 }}>
              {/* Navigation Arrows */}
              <IconButton
                onClick={prevSlide}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: -20,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)'
                  },
                  zIndex: 10
                }}
              >
                {direction === 'rtl' ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
              </IconButton>

              <IconButton
                onClick={nextSlide}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: -20,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)'
                  },
                  zIndex: 10
                }}
              >
                {direction === 'rtl' ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
              </IconButton>

              {/* Cards Container */}
              <Grid container spacing={4}>
                {getVisibleTestimonials().map((testimonial, index) => {
                  const Icon = testimonial.icon;
                  return (
                    <Grid item xs={12} md={4} key={`${testimonial.id}-${index}`}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{
                          y: -10,
                          scale: 1.02,
                          transition: { type: "spring", stiffness: 300 }
                        }}
                        style={{ height: '100%' }}
                      >
                        <Paper
                          elevation={3}
                          sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '20px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Quote Icon */}
                          <FormatQuoteIcon
                            sx={{
                              position: 'absolute',
                              top: 10,
                              [direction === 'rtl' ? 'left' : 'right']: 10,
                              fontSize: 60,
                              color: 'rgba(255,255,255,0.1)',
                              transform: direction === 'rtl' ? 'scaleX(-1)' : 'none'
                            }}
                          />

                          {/* Rating */}
                          <Box sx={{ mb: 2 }}>
                            <Rating
                              value={testimonial.rating}
                              readOnly
                              sx={{
                                '& .MuiRating-iconFilled': {
                                  color: '#FE6B8B'
                                }
                              }}
                            />
                          </Box>

                          {/* Comment */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              color: 'rgba(255,255,255,0.9)',
                              lineHeight: 1.8,
                              mb: 3,
                              flex: 1
                            }}
                          >
                            "{testimonial.comment}"
                          </Typography>

                          {/* Client Info with Text Avatar - لون موحد */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                width: 56,
                                height: 56,
                                bgcolor: AVATAR_COLOR,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                border: '2px solid #FE6B8B'
                              }}
                            >
                              {getInitials(testimonial.name)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  fontWeight: 'bold',
                                  color: 'white'
                                }}
                              >
                                {maskSecondName(testimonial.name)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  color: 'rgba(255,255,255,0.7)',
                                  display: 'block'
                                }}
                              >
                                {testimonial.position}
                              </Typography>
                              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Icon sx={{ fontSize: 14, color: '#FE6B8B' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                    color: 'rgba(255,255,255,0.6)'
                                  }}
                                >
                                  {testimonial.company}
                                </Typography>
                              </Box> */}
                            </Box>
                          </Box>

                          {/* Date */}
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              color: 'rgba(255,255,255,0.5)',
                              display: 'block',
                              textAlign: 'right',
                              mt: 2
                            }}
                          >
                            {testimonial.date}
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Dots Indicator for Desktop */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
                {Array.from({ length: Math.ceil(currentTestimonials.length / 3) }).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentIndex(index * 3)}
                    sx={{
                      width: index === Math.floor(currentIndex / 3) ? 24 : 8,
                      height: 8,
                      borderRadius: index === Math.floor(currentIndex / 3) ? '4px' : '50%',
                      bgcolor: index === Math.floor(currentIndex / 3) ? '#FE6B8B' : 'rgba(255,255,255,0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: '#FE6B8B'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Mobile Carousel View */}
          {isMobile && (
            <Box 
              sx={{ position: 'relative' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Cards Container */}
              <Box sx={{ mx: 2 }}>
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: direction === 'rtl' ? 100 : -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction === 'rtl' ? -100 : 100 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      minHeight: 450,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {(() => {
                      const testimonial = currentTestimonials[currentIndex];
                      const Icon = testimonial.icon;
                      return (
                        <>
                          {/* Quote Icon */}
                          <FormatQuoteIcon
                            sx={{
                              fontSize: 60,
                              color: 'rgba(255,255,255,0.1)',
                              transform: direction === 'rtl' ? 'scaleX(-1)' : 'none',
                              mb: 2
                            }}
                          />

                          {/* Rating */}
                          <Box sx={{ mb: 2 }}>
                            <Rating
                              value={testimonial.rating}
                              readOnly
                              sx={{
                                '& .MuiRating-iconFilled': {
                                  color: '#FE6B8B'
                                }
                              }}
                            />
                          </Box>

                          {/* Comment */}
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              color: 'rgba(255,255,255,0.9)',
                              lineHeight: 1.8,
                              mb: 4,
                              flex: 1
                            }}
                          >
                            "{testimonial.comment}"
                          </Typography>

                          {/* Client Info with Text Avatar - لون موحد */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                width: 64,
                                height: 64,
                                bgcolor: AVATAR_COLOR,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.4rem',
                                border: '2px solid #FE6B8B'
                              }}
                            >
                              {getInitials(testimonial.name)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  fontWeight: 'bold',
                                  color: 'white'
                                }}
                              >
                                {maskSecondName(testimonial.name)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                  color: 'rgba(255,255,255,0.7)'
                                }}
                              >
                                {testimonial.position}
                              </Typography>
                              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Icon sx={{ fontSize: 16, color: '#FE6B8B' }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                                    color: 'rgba(255,255,255,0.6)'
                                  }}
                                >
                                  {testimonial.company}
                                </Typography>
                              </Box> */}
                            </Box>
                          </Box>

                          {/* Date */}
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              color: 'rgba(255,255,255,0.5)',
                              display: 'block',
                              textAlign: 'right',
                              mt: 2
                            }}
                          >
                            {testimonial.date}
                          </Typography>
                        </>
                      );
                    })()}
                  </Paper>
                </motion.div>
              </Box>

              {/* Navigation Arrows for Mobile */}
              <IconButton
                onClick={prevMobile}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: direction === 'rtl' ? 'auto' : 0,
                  right: direction === 'rtl' ? 0 : 'auto',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)'
                  },
                  zIndex: 10
                }}
              >
                {direction === 'rtl' ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
              </IconButton>

              <IconButton
                onClick={nextMobile}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: direction === 'rtl' ? 'auto' : 0,
                  left: direction === 'rtl' ? 0 : 'auto',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)'
                  },
                  zIndex: 10
                }}
              >
                {direction === 'rtl' ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
              </IconButton>

              {/* Dots Indicator for Mobile */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                {currentTestimonials.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    sx={{
                      width: index === currentIndex ? 24 : 8,
                      height: 8,
                      borderRadius: index === currentIndex ? '4px' : '50%',
                      bgcolor: index === currentIndex ? '#FE6B8B' : 'rgba(255,255,255,0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: '#FE6B8B'
                      }
                    }}
                  />
                ))}
              </Box>

              {/* Slide counter for Mobile */}
              <Typography
                variant="caption"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: 'rgba(255,255,255,0.5)',
                  display: 'block',
                  textAlign: 'center',
                  mt: 1
                }}
              >
                {currentIndex + 1} / {currentTestimonials.length}
              </Typography>
            </Box>
          )}

          {/* Stats Counter */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={3} sx={{ mt: 8 }}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: { xs: '2rem', md: '3rem' }
                    }}
                  >
                    150+
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: 'rgba(255,255,255,0.8)'
                    }}
                  >
                    {currentLang === 'ar' ? 'عميل سعيد' : 'Happy Clients'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: { xs: '2rem', md: '3rem' }
                    }}
                  >
                    98%
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: 'rgba(255,255,255,0.8)'
                    }}
                  >
                    {currentLang === 'ar' ? 'تقييم إيجابي' : 'Positive Rating'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: { xs: '2rem', md: '3rem' }
                    }}
                  >
                    5+
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: 'rgba(255,255,255,0.8)'
                    }}
                  >
                    {currentLang === 'ar' ? 'سنوات خبرة' : 'Years Experience'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ClientOpinion;